/**
 * Model Benchmark Data
 *
 * Contains benchmark metrics for all LLM models used in attribute calculation.
 * Extracted from LLMModelSelector to reduce component file size.
 *
 * NO PRICES HERE. A model's price comes from the backend with the model list
 * (GET /api/v1/config/llm-models/{provider} -> LLMModelOption.price_input /
 * price_output), which reads the table the pricer bills. Until 2026-09-25 every
 * row here carried a price hand-copied from OpenRouter; nothing compared it to
 * the billing table, which had 46 prices OpenRouter no longer charged the day
 * it was first checked (RULE #-2: one price table).
 */

export interface ModelBenchmark {
  latency_ms: number;
  throughput_tps: number;
  context_length: number;
  max_output: number;
  parameters_billions: number;
}

export const MODEL_BENCHMARKS: Record<string, ModelBenchmark> = {
  // Aion Labs
  'aion-labs/aion-rp-llama-3.1-8b': {
    latency_ms: 300,
    throughput_tps: 180,
    context_length: 32768,
    max_output: 32768,
    parameters_billions: 8.0,
  },
  // Amazon
  'amazon/nova-lite-v1': {
    latency_ms: 450,
    throughput_tps: 100,
    context_length: 300000,
    max_output: 5120,
    parameters_billions: 50,
  },
  'amazon/nova-micro-v1': {
    latency_ms: 270,
    throughput_tps: 180,
    context_length: 128000,
    max_output: 5120,
    parameters_billions: 3,
  },
  'amazon/nova-pro-v1': {
    latency_ms: 450,
    throughput_tps: 100,
    context_length: 300000,
    max_output: 5120,
    parameters_billions: 50,
  },
  // Anthracite Org
  'anthracite-org/magnum-v4-72b': {
    latency_ms: 600,
    throughput_tps: 60,
    context_length: 16384,
    max_output: 2048,
    parameters_billions: 72.0,
  },
  // Anthropic
  'anthropic/claude-haiku-4.5': {
    latency_ms: 600,
    throughput_tps: 100,
    context_length: 200000,
    max_output: 64000,
    parameters_billions: 20,
  },
  'anthropic/claude-opus-4.1': {
    latency_ms: 900,
    throughput_tps: 40,
    context_length: 200000,
    max_output: 32000,
    parameters_billions: 200,
  },
  'anthropic/claude-sonnet-4': {
    latency_ms: 720,
    throughput_tps: 60,
    context_length: 1000000,
    max_output: 64000,
    parameters_billions: 100,
  },
  'anthropic/claude-sonnet-4.5': {
    latency_ms: 720,
    throughput_tps: 60,
    context_length: 1000000,
    max_output: 64000,
    parameters_billions: 100,
  },
  // Bytedance
  'bytedance/ui-tars-1.5-7b': {
    latency_ms: 300,
    throughput_tps: 180,
    context_length: 128000,
    max_output: 2048,
    parameters_billions: 7.0,
  },
  // Cohere
  'cohere/command-a': {
    latency_ms: 550,
    throughput_tps: 100,
    context_length: 256000,
    max_output: 8192,
    parameters_billions: 50,
  },
  'cohere/command-r-08-2024': {
    latency_ms: 550,
    throughput_tps: 100,
    context_length: 128000,
    max_output: 4000,
    parameters_billions: 50,
  },
  'cohere/command-r-plus-08-2024': {
    latency_ms: 550,
    throughput_tps: 100,
    context_length: 128000,
    max_output: 4000,
    parameters_billions: 50,
  },
  'cohere/command-r7b-12-2024': {
    latency_ms: 330,
    throughput_tps: 180,
    context_length: 128000,
    max_output: 4000,
    parameters_billions: 7.0,
  },
  // Deepseek
  'deepseek/deepseek-chat': {
    latency_ms: 500,
    throughput_tps: 100,
    context_length: 163840,
    max_output: 163840,
    parameters_billions: 50,
  },
  'deepseek/deepseek-chat-v3-0324': {
    latency_ms: 500,
    throughput_tps: 100,
    context_length: 163840,
    max_output: 65536,
    parameters_billions: 50,
  },
  'deepseek/deepseek-chat-v3.1': {
    latency_ms: 500,
    throughput_tps: 100,
    context_length: 32768,
    max_output: 7168,
    parameters_billions: 50,
  },
  'deepseek/deepseek-r1': {
    latency_ms: 500,
    throughput_tps: 100,
    context_length: 64000,
    max_output: 16000,
    parameters_billions: 50,
  },
  'deepseek/deepseek-r1-0528': {
    latency_ms: 500,
    throughput_tps: 100,
    context_length: 163840,
    max_output: 65536,
    parameters_billions: 50,
  },
  'deepseek/deepseek-v3.1-terminus': {
    latency_ms: 500,
    throughput_tps: 100,
    context_length: 163840,
    max_output: 81920,
    parameters_billions: 50,
  },
  'deepseek/deepseek-v3.2-exp': {
    latency_ms: 500,
    throughput_tps: 100,
    context_length: 163840,
    max_output: 65536,
    parameters_billions: 50,
  },
  // Google
  'google/gemini-2.5-flash': {
    latency_ms: 210,
    throughput_tps: 180,
    context_length: 1048576,
    max_output: 65535,
    parameters_billions: 7,
  },
  'google/gemini-2.5-flash-lite': {
    latency_ms: 210,
    throughput_tps: 180,
    context_length: 1048576,
    max_output: 65535,
    parameters_billions: 7,
  },
  'google/gemini-2.5-pro': {
    latency_ms: 210,
    throughput_tps: 180,
    context_length: 1048576,
    max_output: 65536,
    parameters_billions: 7,
  },
  'google/gemini-2.5-pro-preview': {
    latency_ms: 210,
    throughput_tps: 180,
    context_length: 1048576,
    max_output: 65536,
    parameters_billions: 7,
  },
  'google/gemma-2-27b-it': {
    latency_ms: 350,
    throughput_tps: 100,
    context_length: 8192,
    max_output: 2048,
    parameters_billions: 27.0,
  },
  'google/gemma-3-12b-it': {
    latency_ms: 350,
    throughput_tps: 100,
    context_length: 131072,
    max_output: 131072,
    parameters_billions: 12.0,
  },
  'google/gemma-3-27b-it': {
    latency_ms: 350,
    throughput_tps: 100,
    context_length: 96000,
    max_output: 96000,
    parameters_billions: 27.0,
  },
  'google/gemma-3-4b-it': {
    latency_ms: 210,
    throughput_tps: 180,
    context_length: 96000,
    max_output: 48000,
    parameters_billions: 4.0,
  },
  'google/gemma-4-26b-a4b-it:free': {
    latency_ms: 300,
    throughput_tps: 120,
    context_length: 262144,
    max_output: 65536,
    parameters_billions: 26.0,
  },
  'google/gemma-4-31b-it:free': {
    latency_ms: 350,
    throughput_tps: 110,
    context_length: 262144,
    max_output: 65536,
    parameters_billions: 31.0,
  },
  // google/lyria-3-* removed 2026-05-22: music models, not chat LLMs — see /api/v1/music/*
  // Gryphe
  'gryphe/mythomax-l2-13b': {
    latency_ms: 500,
    throughput_tps: 100,
    context_length: 4096,
    max_output: 4096,
    parameters_billions: 13.0,
  },
  // Ibm Granite
  'ibm-granite/granite-4.0-h-micro': {
    latency_ms: 300,
    throughput_tps: 180,
    context_length: 131000,
    max_output: 65500,
    parameters_billions: 3,
  },
  // Mancer
  'mancer/weaver': {
    latency_ms: 500,
    throughput_tps: 100,
    context_length: 8000,
    max_output: 2000,
    parameters_billions: 50,
  },
  // Meta Llama
  'meta-llama/llama-3.1-70b-instruct': {
    latency_ms: 360,
    throughput_tps: 60,
    context_length: 131072,
    max_output: 65536,
    parameters_billions: 70.0,
  },
  'meta-llama/llama-3.1-8b-instruct': {
    latency_ms: 180,
    throughput_tps: 180,
    context_length: 16384,
    max_output: 16384,
    parameters_billions: 8.0,
  },
  'meta-llama/llama-3.2-1b-instruct': {
    latency_ms: 180,
    throughput_tps: 180,
    context_length: 60000,
    max_output: 30000,
    parameters_billions: 1.0,
  },
  'meta-llama/llama-3.2-3b-instruct': {
    latency_ms: 180,
    throughput_tps: 180,
    context_length: 131072,
    max_output: 16384,
    parameters_billions: 3.0,
  },
  'meta-llama/llama-3.3-70b-instruct': {
    latency_ms: 360,
    throughput_tps: 60,
    context_length: 131072,
    max_output: 16384,
    parameters_billions: 70.0,
  },
  'meta-llama/llama-4-maverick': {
    latency_ms: 300,
    throughput_tps: 100,
    context_length: 1048576,
    max_output: 16384,
    parameters_billions: 50,
  },
  'meta-llama/llama-4-scout': {
    latency_ms: 300,
    throughput_tps: 100,
    context_length: 327680,
    max_output: 16384,
    parameters_billions: 50,
  },
  'meta-llama/llama-guard-4-12b': {
    latency_ms: 300,
    throughput_tps: 100,
    context_length: 163840,
    max_output: 81920,
    parameters_billions: 12.0,
  },
  // Microsoft
  'microsoft/phi-4': {
    latency_ms: 300,
    throughput_tps: 180,
    context_length: 16384,
    max_output: 16384,
    parameters_billions: 3,
  },
  'microsoft/wizardlm-2-8x22b': {
    latency_ms: 500,
    throughput_tps: 100,
    context_length: 65536,
    max_output: 16384,
    parameters_billions: 22.0,
  },
  // Minimax
  'minimax/minimax-01': {
    latency_ms: 300,
    throughput_tps: 180,
    context_length: 1000192,
    max_output: 1000192,
    parameters_billions: 7,
  },
  'minimax/minimax-m1': {
    latency_ms: 300,
    throughput_tps: 180,
    context_length: 1000000,
    max_output: 40000,
    parameters_billions: 7,
  },
  // Mistralai
  'mistralai/codestral-2508': {
    latency_ms: 400,
    throughput_tps: 100,
    context_length: 256000,
    max_output: 128000,
    parameters_billions: 50,
  },
  'mistralai/mistral-large': {
    latency_ms: 480,
    throughput_tps: 60,
    context_length: 128000,
    max_output: 64000,
    parameters_billions: 70,
  },
  'mistralai/mistral-large-2407': {
    latency_ms: 480,
    throughput_tps: 60,
    context_length: 131072,
    max_output: 65536,
    parameters_billions: 70,
  },
  'mistralai/mistral-medium-3': {
    latency_ms: 400,
    throughput_tps: 100,
    context_length: 131072,
    max_output: 65536,
    parameters_billions: 20,
  },
  'mistralai/mistral-medium-3.1': {
    latency_ms: 400,
    throughput_tps: 100,
    context_length: 131072,
    max_output: 65536,
    parameters_billions: 20,
  },
  'mistralai/mistral-nemo': {
    latency_ms: 400,
    throughput_tps: 100,
    context_length: 131072,
    max_output: 16384,
    parameters_billions: 50,
  },
  'mistralai/mistral-saba': {
    latency_ms: 400,
    throughput_tps: 100,
    context_length: 32768,
    max_output: 16384,
    parameters_billions: 50,
  },
  'mistralai/mistral-small-24b-instruct-2501': {
    latency_ms: 400,
    throughput_tps: 100,
    context_length: 32768,
    max_output: 32768,
    parameters_billions: 24.0,
  },
  'mistralai/mistral-small-3.1-24b-instruct': {
    latency_ms: 400,
    throughput_tps: 100,
    context_length: 131072,
    max_output: 131072,
    parameters_billions: 24.0,
  },
  'mistralai/mistral-small-3.2-24b-instruct': {
    latency_ms: 400,
    throughput_tps: 100,
    context_length: 131072,
    max_output: 131072,
    parameters_billions: 24.0,
  },
  'mistralai/mixtral-8x22b-instruct': {
    latency_ms: 400,
    throughput_tps: 100,
    context_length: 65536,
    max_output: 32768,
    parameters_billions: 22.0,
  },
  // Moonshotai
  'moonshotai/kimi-k2': {
    latency_ms: 500,
    throughput_tps: 100,
    context_length: 131072,
    max_output: 65536,
    parameters_billions: 50,
  },
  'moonshotai/kimi-k2-0905': {
    latency_ms: 500,
    throughput_tps: 100,
    context_length: 262144,
    max_output: 262144,
    parameters_billions: 50,
  },
  // Morph
  'morph/morph-v3-fast': {
    latency_ms: 500,
    throughput_tps: 100,
    context_length: 81920,
    max_output: 38000,
    parameters_billions: 50,
  },
  'morph/morph-v3-large': {
    latency_ms: 600,
    throughput_tps: 60,
    context_length: 262144,
    max_output: 131072,
    parameters_billions: 70,
  },
  // Nousresearch
  'nousresearch/hermes-3-llama-3.1-405b': {
    latency_ms: 600,
    throughput_tps: 40,
    context_length: 131072,
    max_output: 16384,
    parameters_billions: 405.0,
  },
  'nousresearch/hermes-3-llama-3.1-70b': {
    latency_ms: 480,
    throughput_tps: 60,
    context_length: 65536,
    max_output: 65536,
    parameters_billions: 70.0,
  },
  'nousresearch/hermes-4-405b': {
    latency_ms: 600,
    throughput_tps: 40,
    context_length: 131072,
    max_output: 65536,
    parameters_billions: 405.0,
  },
  // Nvidia
  'nvidia/nemotron-3-super-120b-a12b:free': {
    latency_ms: 400,
    throughput_tps: 80,
    context_length: 262144,
    max_output: 65536,
    parameters_billions: 120.0,
  },
  // Openai
  'openai/gpt-3.5-turbo': {
    latency_ms: 400,
    throughput_tps: 100,
    context_length: 16385,
    max_output: 4096,
    parameters_billions: 20,
  },
  'openai/gpt-3.5-turbo-0613': {
    latency_ms: 400,
    throughput_tps: 100,
    context_length: 4095,
    max_output: 4096,
    parameters_billions: 20,
  },
  'openai/gpt-3.5-turbo-16k': {
    latency_ms: 400,
    throughput_tps: 100,
    context_length: 16385,
    max_output: 4096,
    parameters_billions: 20,
  },
  'openai/gpt-3.5-turbo-instruct': {
    latency_ms: 400,
    throughput_tps: 100,
    context_length: 4095,
    max_output: 4096,
    parameters_billions: 20,
  },
  'openai/gpt-4': {
    latency_ms: 600,
    throughput_tps: 40,
    context_length: 8191,
    max_output: 4096,
    parameters_billions: 175,
  },
  'openai/gpt-4-turbo': {
    latency_ms: 600,
    throughput_tps: 40,
    context_length: 128000,
    max_output: 4096,
    parameters_billions: 175,
  },
  'openai/gpt-4.1': {
    latency_ms: 600,
    throughput_tps: 40,
    context_length: 1047576,
    max_output: 32768,
    parameters_billions: 200,
  },
  'openai/gpt-4.1-mini': {
    latency_ms: 240,
    throughput_tps: 180,
    context_length: 1047576,
    max_output: 32768,
    parameters_billions: 7,
  },
  'openai/gpt-4.1-nano': {
    latency_ms: 240,
    throughput_tps: 180,
    context_length: 1047576,
    max_output: 32768,
    parameters_billions: 1,
  },
  'openai/gpt-4o': {
    latency_ms: 600,
    throughput_tps: 40,
    context_length: 128000,
    max_output: 16384,
    parameters_billions: 175,
  },
  'openai/gpt-4o-2024-05-13': {
    latency_ms: 600,
    throughput_tps: 40,
    context_length: 128000,
    max_output: 4096,
    parameters_billions: 175,
  },
  'openai/gpt-4o-2024-08-06': {
    latency_ms: 600,
    throughput_tps: 40,
    context_length: 128000,
    max_output: 16384,
    parameters_billions: 175,
  },
  'openai/gpt-4o-2024-11-20': {
    latency_ms: 600,
    throughput_tps: 40,
    context_length: 128000,
    max_output: 16384,
    parameters_billions: 175,
  },
  'openai/gpt-4o-mini': {
    latency_ms: 240,
    throughput_tps: 180,
    context_length: 128000,
    max_output: 16384,
    parameters_billions: 7,
  },
  'openai/gpt-4o-mini-2024-07-18': {
    latency_ms: 240,
    throughput_tps: 180,
    context_length: 128000,
    max_output: 16384,
    parameters_billions: 7,
  },
  'openai/gpt-5': {
    latency_ms: 600,
    throughput_tps: 40,
    context_length: 400000,
    max_output: 128000,
    parameters_billions: 500,
  },
  'openai/gpt-5-image': {
    latency_ms: 600,
    throughput_tps: 40,
    context_length: 400000,
    max_output: 128000,
    parameters_billions: 500,
  },
  'openai/gpt-5-image-mini': {
    latency_ms: 240,
    throughput_tps: 180,
    context_length: 400000,
    max_output: 128000,
    parameters_billions: 7,
  },
  'openai/gpt-5-mini': {
    latency_ms: 240,
    throughput_tps: 180,
    context_length: 400000,
    max_output: 128000,
    parameters_billions: 7,
  },
  'openai/gpt-5-pro': {
    latency_ms: 600,
    throughput_tps: 40,
    context_length: 400000,
    max_output: 128000,
    parameters_billions: 500,
  },
  'openai/gpt-oss-120b': {
    latency_ms: 600,
    throughput_tps: 40,
    context_length: 131072,
    max_output: 65536,
    parameters_billions: 120.0,
  },
  'openai/gpt-oss-20b': {
    latency_ms: 400,
    throughput_tps: 100,
    context_length: 131072,
    max_output: 131072,
    parameters_billions: 20.0,
  },
  'openai/o1': {
    latency_ms: 400,
    throughput_tps: 100,
    context_length: 200000,
    max_output: 100000,
    parameters_billions: 50,
  },
  'openai/o1-pro': {
    latency_ms: 400,
    throughput_tps: 100,
    context_length: 200000,
    max_output: 100000,
    parameters_billions: 50,
  },
  'openai/o3': {
    latency_ms: 400,
    throughput_tps: 100,
    context_length: 200000,
    max_output: 100000,
    parameters_billions: 50,
  },
  'openai/o3-mini': {
    latency_ms: 240,
    throughput_tps: 180,
    context_length: 200000,
    max_output: 100000,
    parameters_billions: 7,
  },
  'openai/o3-mini-high': {
    latency_ms: 240,
    throughput_tps: 180,
    context_length: 200000,
    max_output: 100000,
    parameters_billions: 7,
  },
  'openai/o3-pro': {
    latency_ms: 400,
    throughput_tps: 100,
    context_length: 200000,
    max_output: 100000,
    parameters_billions: 50,
  },
  'openai/o4-mini': {
    latency_ms: 240,
    throughput_tps: 180,
    context_length: 200000,
    max_output: 100000,
    parameters_billions: 7,
  },
  'openai/o4-mini-high': {
    latency_ms: 240,
    throughput_tps: 180,
    context_length: 200000,
    max_output: 100000,
    parameters_billions: 7,
  },
  // Openrouter
  'openrouter/auto': {
    latency_ms: 500,
    throughput_tps: 100,
    context_length: 2000000,
    max_output: 128000,
    parameters_billions: 50,
  },
  // Perplexity
  'perplexity/sonar': {
    latency_ms: 500,
    throughput_tps: 100,
    context_length: 127072,
    max_output: 63536,
    parameters_billions: 50,
  },
  'perplexity/sonar-deep-research': {
    latency_ms: 500,
    throughput_tps: 100,
    context_length: 128000,
    max_output: 64000,
    parameters_billions: 50,
  },
  'perplexity/sonar-pro': {
    latency_ms: 500,
    throughput_tps: 100,
    context_length: 200000,
    max_output: 8000,
    parameters_billions: 50,
  },
  'perplexity/sonar-reasoning-pro': {
    latency_ms: 500,
    throughput_tps: 100,
    context_length: 128000,
    max_output: 64000,
    parameters_billions: 50,
  },
  // Qwen
  'qwen/qwen-2.5-72b-instruct': {
    latency_ms: 540,
    throughput_tps: 60,
    context_length: 32768,
    max_output: 16384,
    parameters_billions: 72.0,
  },
  'qwen/qwen-2.5-7b-instruct': {
    latency_ms: 270,
    throughput_tps: 180,
    context_length: 32768,
    max_output: 16384,
    parameters_billions: 7.0,
  },
  'qwen/qwen-2.5-coder-32b-instruct': {
    latency_ms: 450,
    throughput_tps: 100,
    context_length: 32768,
    max_output: 32768,
    parameters_billions: 32.0,
  },
  'qwen/qwen-plus': {
    latency_ms: 450,
    throughput_tps: 100,
    context_length: 131072,
    max_output: 8192,
    parameters_billions: 50,
  },
  'qwen/qwen-plus-2025-07-28': {
    latency_ms: 450,
    throughput_tps: 100,
    context_length: 1000000,
    max_output: 32768,
    parameters_billions: 50,
  },
  'qwen/qwen2.5-vl-72b-instruct': {
    latency_ms: 540,
    throughput_tps: 60,
    context_length: 32768,
    max_output: 32768,
    parameters_billions: 72.0,
  },
  'qwen/qwen3-14b': {
    latency_ms: 450,
    throughput_tps: 100,
    context_length: 40960,
    max_output: 40960,
    parameters_billions: 14.0,
  },
  'qwen/qwen3-235b-a22b': {
    latency_ms: 675,
    throughput_tps: 40,
    context_length: 40960,
    max_output: 20480,
    parameters_billions: 235.0,
  },
  'qwen/qwen3-235b-a22b-2507': {
    latency_ms: 675,
    throughput_tps: 40,
    context_length: 262144,
    max_output: 128000,
    parameters_billions: 235.0,
  },
  'qwen/qwen3-235b-a22b-thinking-2507': {
    latency_ms: 675,
    throughput_tps: 40,
    context_length: 262144,
    max_output: 262144,
    parameters_billions: 235.0,
  },
  'qwen/qwen3-30b-a3b': {
    latency_ms: 450,
    throughput_tps: 100,
    context_length: 40960,
    max_output: 40960,
    parameters_billions: 30.0,
  },
  'qwen/qwen3-30b-a3b-instruct-2507': {
    latency_ms: 450,
    throughput_tps: 100,
    context_length: 262144,
    max_output: 262144,
    parameters_billions: 30.0,
  },
  'qwen/qwen3-30b-a3b-thinking-2507': {
    latency_ms: 450,
    throughput_tps: 100,
    context_length: 32768,
    max_output: 16384,
    parameters_billions: 30.0,
  },
  'qwen/qwen3-32b': {
    latency_ms: 450,
    throughput_tps: 100,
    context_length: 40960,
    max_output: 40960,
    parameters_billions: 32.0,
  },
  'qwen/qwen3-8b': {
    latency_ms: 270,
    throughput_tps: 180,
    context_length: 32000,
    max_output: 8192,
    parameters_billions: 8.0,
  },
  'qwen/qwen3-coder': {
    latency_ms: 450,
    throughput_tps: 100,
    context_length: 262144,
    max_output: 262144,
    parameters_billions: 50,
  },
  'qwen/qwen3-coder-30b-a3b-instruct': {
    latency_ms: 450,
    throughput_tps: 100,
    context_length: 160000,
    max_output: 32768,
    parameters_billions: 30.0,
  },
  'qwen/qwen3-coder-flash': {
    latency_ms: 450,
    throughput_tps: 100,
    context_length: 128000,
    max_output: 65536,
    parameters_billions: 50,
  },
  'qwen/qwen3-coder-plus': {
    latency_ms: 450,
    throughput_tps: 100,
    context_length: 128000,
    max_output: 65536,
    parameters_billions: 50,
  },
  'qwen/qwen3-max': {
    latency_ms: 450,
    throughput_tps: 100,
    context_length: 256000,
    max_output: 32768,
    parameters_billions: 50,
  },
  'qwen/qwen3-next-80b-a3b-instruct': {
    latency_ms: 540,
    throughput_tps: 60,
    context_length: 262144,
    max_output: 128000,
    parameters_billions: 80.0,
  },
  'qwen/qwen3-next-80b-a3b-thinking': {
    latency_ms: 540,
    throughput_tps: 60,
    context_length: 128000,
    max_output: 64000,
    parameters_billions: 80.0,
  },
  'qwen/qwen3-vl-235b-a22b-instruct': {
    latency_ms: 675,
    throughput_tps: 40,
    context_length: 262144,
    max_output: 128000,
    parameters_billions: 235.0,
  },
  'qwen/qwen3-vl-235b-a22b-thinking': {
    latency_ms: 675,
    throughput_tps: 40,
    context_length: 262144,
    max_output: 262144,
    parameters_billions: 235.0,
  },
  'qwen/qwen3-vl-30b-a3b-instruct': {
    latency_ms: 450,
    throughput_tps: 100,
    context_length: 262144,
    max_output: 128000,
    parameters_billions: 30.0,
  },
  'qwen/qwen3-vl-30b-a3b-thinking': {
    latency_ms: 450,
    throughput_tps: 100,
    context_length: 131072,
    max_output: 32768,
    parameters_billions: 30.0,
  },
  'qwen/qwen3-vl-32b-instruct': {
    latency_ms: 450,
    throughput_tps: 100,
    context_length: 262144,
    max_output: 128000,
    parameters_billions: 32.0,
  },
  'qwen/qwen3-vl-8b-instruct': {
    latency_ms: 270,
    throughput_tps: 180,
    context_length: 131072,
    max_output: 32768,
    parameters_billions: 8.0,
  },
  'qwen/qwen3-vl-8b-thinking': {
    latency_ms: 270,
    throughput_tps: 180,
    context_length: 256000,
    max_output: 32768,
    parameters_billions: 8.0,
  },
  // Relace
  'relace/relace-apply-3': {
    latency_ms: 500,
    throughput_tps: 100,
    context_length: 256000,
    max_output: 128000,
    parameters_billions: 50,
  },
  // Sao10K
  'sao10k/l3-lunaris-8b': {
    latency_ms: 300,
    throughput_tps: 180,
    context_length: 8192,
    max_output: 4096,
    parameters_billions: 8.0,
  },
  'sao10k/l3.1-euryale-70b': {
    latency_ms: 600,
    throughput_tps: 60,
    context_length: 32768,
    max_output: 32768,
    parameters_billions: 70.0,
  },
  'sao10k/l3.3-euryale-70b': {
    latency_ms: 600,
    throughput_tps: 60,
    context_length: 131072,
    max_output: 16384,
    parameters_billions: 70.0,
  },
  // Tencent
  'tencent/hunyuan-a13b-instruct': {
    latency_ms: 500,
    throughput_tps: 100,
    context_length: 131072,
    max_output: 131072,
    parameters_billions: 13.0,
  },
  // Thedrummer
  'thedrummer/cydonia-24b-v4.1': {
    latency_ms: 500,
    throughput_tps: 100,
    context_length: 131072,
    max_output: 131072,
    parameters_billions: 24.0,
  },
  'thedrummer/skyfall-36b-v2': {
    latency_ms: 500,
    throughput_tps: 100,
    context_length: 32768,
    max_output: 32768,
    parameters_billions: 36.0,
  },
  'thedrummer/unslopnemo-12b': {
    latency_ms: 500,
    throughput_tps: 100,
    context_length: 32768,
    max_output: 32768,
    parameters_billions: 12.0,
  },
  // Undi95
  'undi95/remm-slerp-l2-13b': {
    latency_ms: 500,
    throughput_tps: 100,
    context_length: 6144,
    max_output: 4096,
    parameters_billions: 13.0,
  },
  // Z Ai
  'z-ai/glm-4.5': {
    latency_ms: 500,
    throughput_tps: 100,
    context_length: 131072,
    max_output: 65536,
    parameters_billions: 50,
  },
  'z-ai/glm-4.5-air': {
    latency_ms: 500,
    throughput_tps: 100,
    context_length: 131072,
    max_output: 131072,
    parameters_billions: 50,
  },
  'z-ai/glm-4.5v': {
    latency_ms: 500,
    throughput_tps: 100,
    context_length: 65536,
    max_output: 16384,
    parameters_billions: 50,
  },
  'z-ai/glm-4.6': {
    latency_ms: 500,
    throughput_tps: 100,
    context_length: 202752,
    max_output: 65536,
    parameters_billions: 50,
  },
  'openrouter/free': {
    latency_ms: 500,
    throughput_tps: 80,
    context_length: 200000,
    max_output: 200000,
    parameters_billions: 0,
  },

  // NEW 2026-06-10
  'nvidia/nemotron-3-ultra-550b-a55b:free': {
    latency_ms: 2000,
    throughput_tps: 20,
    context_length: 1000000,
    max_output: 131072,
    parameters_billions: 550,
  },
  'nvidia/nemotron-3.5-content-safety:free': {
    latency_ms: 300,
    throughput_tps: 94,
    context_length: 128000,
    max_output: 32768,
    parameters_billions: 0,
  },

  // NEW 2026-07-08
  'cohere/north-mini-code:free': {
    latency_ms: 400,
    throughput_tps: 68,
    context_length: 256000,
    max_output: 32768,
    parameters_billions: 0,
  },
  'poolside/laguna-xs-2.1:free': {
    latency_ms: 400,
    throughput_tps: 67,
    context_length: 262144,
    max_output: 32768,
    parameters_billions: 0,
  },
  'poolside/laguna-s-2.1:free': {
    latency_ms: 400,
    throughput_tps: 67,
    context_length: 262144,
    max_output: 32768,
    parameters_billions: 0,
  },
  'liquid/lfm-2.5-2.6b:free': {
    latency_ms: 150,
    throughput_tps: 150,
    context_length: 128000,
    max_output: 16384,
    parameters_billions: 2.6,
  },
  'nvidia/nemotron-3.5-lightning:free': {
    latency_ms: 350,
    throughput_tps: 80,
    context_length: 1000000,
    max_output: 32768,
    parameters_billions: 0,
  },
  'dots-studio/dots-3-note-preview:free': {
    latency_ms: 600,
    throughput_tps: 17,
    context_length: 512000,
    max_output: 32768,
    parameters_billions: 0,
  },

  // --- NEW 2026-08-26 ---
  'thinkingmachines/inkling-small:free': {
    latency_ms: 250,
    throughput_tps: 120,
    context_length: 1048576,
    max_output: 32768,
    parameters_billions: 0,
  },
  'thinkingmachines/inkling:free': {
    latency_ms: 400,
    throughput_tps: 80,
    context_length: 1048576,
    max_output: 32768,
    parameters_billions: 0,
  },

  // ==========================================================================
  // latency_ms / throughput_tps are local-GPU estimates, monotonic with size
  // ==========================================================================

  // Gemma 3 series
  'gemma3:1b': {
    latency_ms: 150,
    throughput_tps: 250,
    context_length: 131072,
    max_output: 8192,
    parameters_billions: 1,
  },
  'gemma3:4b': {
    latency_ms: 280,
    throughput_tps: 140,
    context_length: 131072,
    max_output: 8192,
    parameters_billions: 4,
  },
  'gemma3:12b': {
    latency_ms: 500,
    throughput_tps: 70,
    context_length: 131072,
    max_output: 8192,
    parameters_billions: 12,
  },
  'gemma3:27b': {
    latency_ms: 750,
    throughput_tps: 40,
    context_length: 131072,
    max_output: 8192,
    parameters_billions: 27,
  },

  // Gemma 2 series
  'gemma2:2b': {
    latency_ms: 160,
    throughput_tps: 230,
    context_length: 8192,
    max_output: 8192,
    parameters_billions: 2,
  },
  'gemma2:9b': {
    latency_ms: 380,
    throughput_tps: 100,
    context_length: 8192,
    max_output: 8192,
    parameters_billions: 9,
  },

  // Gemma 1 (legacy)
  'gemma:2b': {
    latency_ms: 160,
    throughput_tps: 230,
    context_length: 8192,
    max_output: 4096,
    parameters_billions: 2,
  },
  'gemma:7b': {
    latency_ms: 320,
    throughput_tps: 110,
    context_length: 8192,
    max_output: 4096,
    parameters_billions: 7,
  },

  // Qwen3 series
  'qwen3:8b': {
    latency_ms: 340,
    throughput_tps: 120,
    context_length: 131072,
    max_output: 8192,
    parameters_billions: 8,
  },
  'qwen3:14b': {
    latency_ms: 520,
    throughput_tps: 70,
    context_length: 131072,
    max_output: 8192,
    parameters_billions: 14,
  },
  'qwen3:32b': {
    latency_ms: 800,
    throughput_tps: 38,
    context_length: 131072,
    max_output: 8192,
    parameters_billions: 32,
  },

  // Qwen 2.5 series
  'qwen2.5:3b': {
    latency_ms: 180,
    throughput_tps: 210,
    context_length: 131072,
    max_output: 8192,
    parameters_billions: 3,
  },
  'qwen2.5:7b': {
    latency_ms: 310,
    throughput_tps: 120,
    context_length: 131072,
    max_output: 8192,
    parameters_billions: 7,
  },
  'qwen2.5:14b': {
    latency_ms: 520,
    throughput_tps: 70,
    context_length: 131072,
    max_output: 8192,
    parameters_billions: 14,
  },
  'qwen2.5:32b': {
    latency_ms: 800,
    throughput_tps: 38,
    context_length: 131072,
    max_output: 8192,
    parameters_billions: 32,
  },
  'qwen2.5-coder:7b': {
    latency_ms: 310,
    throughput_tps: 120,
    context_length: 131072,
    max_output: 8192,
    parameters_billions: 7,
  },
  'qwen2.5-coder:14b': {
    latency_ms: 520,
    throughput_tps: 70,
    context_length: 131072,
    max_output: 8192,
    parameters_billions: 14,
  },

  // DeepSeek-R1 distill series
  'deepseek-r1:7b': {
    latency_ms: 320,
    throughput_tps: 110,
    context_length: 131072,
    max_output: 8192,
    parameters_billions: 7,
  },
  'deepseek-r1:8b': {
    latency_ms: 340,
    throughput_tps: 105,
    context_length: 131072,
    max_output: 8192,
    parameters_billions: 8,
  },
  'deepseek-r1:14b': {
    latency_ms: 540,
    throughput_tps: 65,
    context_length: 131072,
    max_output: 8192,
    parameters_billions: 14,
  },
  'deepseek-r1:32b': {
    latency_ms: 820,
    throughput_tps: 36,
    context_length: 131072,
    max_output: 8192,
    parameters_billions: 32,
  },

  // Llama 3.x series
  'llama3.2:1b': {
    latency_ms: 145,
    throughput_tps: 260,
    context_length: 131072,
    max_output: 8192,
    parameters_billions: 1,
  },
  'llama3.2:3b': {
    latency_ms: 185,
    throughput_tps: 200,
    context_length: 131072,
    max_output: 8192,
    parameters_billions: 3,
  },
  'llama3.1:8b': {
    latency_ms: 330,
    throughput_tps: 115,
    context_length: 131072,
    max_output: 8192,
    parameters_billions: 8,
  },
  'llama3:8b': {
    latency_ms: 335,
    throughput_tps: 112,
    context_length: 8192,
    max_output: 8192,
    parameters_billions: 8,
  },
  'llama3.3:70b': {
    latency_ms: 950,
    throughput_tps: 30,
    context_length: 131072,
    max_output: 8192,
    parameters_billions: 70,
  },

  // Llama 2 series
  'llama2:7b': {
    latency_ms: 320,
    throughput_tps: 115,
    context_length: 4096,
    max_output: 4096,
    parameters_billions: 7,
  },
  'llama2:13b': {
    latency_ms: 530,
    throughput_tps: 68,
    context_length: 4096,
    max_output: 4096,
    parameters_billions: 13,
  },
  'llama2:70b': {
    latency_ms: 960,
    throughput_tps: 30,
    context_length: 4096,
    max_output: 4096,
    parameters_billions: 70,
  },

  // Mistral series
  'mistral:7b': {
    latency_ms: 310,
    throughput_tps: 120,
    context_length: 32768,
    max_output: 8192,
    parameters_billions: 7,
  },
  'mistral-nemo:12b': {
    latency_ms: 490,
    throughput_tps: 75,
    context_length: 128000,
    max_output: 8192,
    parameters_billions: 12,
  },
  'mistral-small:24b': {
    latency_ms: 680,
    throughput_tps: 48,
    context_length: 131072,
    max_output: 8192,
    parameters_billions: 24,
  },
  'mixtral:8x7b': {
    latency_ms: 700,
    throughput_tps: 45,
    context_length: 32768,
    max_output: 8192,
    parameters_billions: 7,
  },

  // Microsoft Phi
  'phi4:14b': {
    latency_ms: 530,
    throughput_tps: 68,
    context_length: 131072,
    max_output: 8192,
    parameters_billions: 14,
  },
  'phi:2.7b': {
    latency_ms: 170,
    throughput_tps: 220,
    context_length: 4096,
    max_output: 4096,
    parameters_billions: 3,
  },

  // Other chat models
  'neural-chat:7b': {
    latency_ms: 320,
    throughput_tps: 115,
    context_length: 8192,
    max_output: 4096,
    parameters_billions: 7,
  },
  'starling-lm:7b': {
    latency_ms: 320,
    throughput_tps: 115,
    context_length: 8192,
    max_output: 4096,
    parameters_billions: 7,
  },

  // Code models
  'codellama:7b': {
    latency_ms: 320,
    throughput_tps: 115,
    context_length: 16384,
    max_output: 4096,
    parameters_billions: 7,
  },
  'codellama:13b': {
    latency_ms: 530,
    throughput_tps: 68,
    context_length: 16384,
    max_output: 4096,
    parameters_billions: 13,
  },
  'codellama:34b': {
    latency_ms: 840,
    throughput_tps: 35,
    context_length: 16384,
    max_output: 4096,
    parameters_billions: 34,
  },

  // ==========================================================================
  // RunPod public endpoints (prices: constants/llm/runpod_public_llm.py)
  // ==========================================================================

  'granite-4-0-h-small': {
    latency_ms: 350,
    throughput_tps: 120,
    context_length: 128000,
    max_output: 65536,
    parameters_billions: 32,
  },
  'qwen3-32b-awq': {
    latency_ms: 400,
    throughput_tps: 110,
    context_length: 131072,
    max_output: 65536,
    parameters_billions: 32,
  },
  'nvidia/nemotron-3-nano-omni-30b-a3b-reasoning:free': {
    latency_ms: 1200,
    throughput_tps: 68,
    context_length: 256000,
    max_output: 32768,
    parameters_billions: 30,
  },
  'qwen/qwen3.8-27b:free': {
    latency_ms: 800,
    throughput_tps: 67,
    context_length: 262144,
    max_output: 32768,
    parameters_billions: 27,
  },

  // Added by sync_models.py --mode frontend --update on 2026-10-07.
  // latency_ms, throughput_tps and parameters_billions are ESTIMATES from the id;
  // context_length and max_output are OpenRouter's.
  'apodex/apodex-1.1-mini:free': {
    latency_ms: 300,
    throughput_tps: 180,
    context_length: 262144,
    max_output: 235929,
    parameters_billions: 7,
  },
  'inclusionai/ling-3.1-flash': {
    latency_ms: 500,
    throughput_tps: 100,
    context_length: 262144,
    max_output: 32768,
    parameters_billions: 50,
  },
};

// Reference values for normalization
export const BENCHMARK_REFS = {
  latency_min: 100,
  latency_max: 2500,
  throughput_min: 30,
  throughput_max: 300,
  context_min: 8192,
  context_max: 2000000,
  output_min: 4096,
  output_max: 163840,
  // Scale of the ATK/DEF bars ($/1M tokens at which a bar is full), not prices.
  price_input_max: 30.0,
  price_output_max: 80.0,
};
